import { useState, useEffect, useRef, useCallback } from 'react'
import { API_URL, getAuthHeaders } from '../api'
import { getToken } from '../api'

const WS_URL = API_URL.replace('http', 'ws').replace('https', 'wss')

// Resolve avatar URLs for in-app notifications. Relative /api/... paths need
// the backend API origin because the browser notification may be shown from
// the frontend origin.
const CDN_URL = 'https://cdn.colossus7.com'
const avatarUrl = (url: string | undefined) => {
  if (!url) return '/atlas.webp'
  if (url.startsWith('/api/media/')) return CDN_URL + '/' + url.slice('/api/media/'.length)
  if (url.startsWith('/api/')) return API_URL + url
  return url
}

// Generate a short notification beep using Web Audio API
let lastSoundTime = 0
function playNotificationSound() {
  // Throttle: only beep if 5s have passed since the last beep.
  // Consecutive messages within 5s are silently suppressed — no
  // delayed second beep. After 5s of silence the throttle resets.
  const now = Date.now()
  if (now - lastSoundTime >= 5000) {
    lastSoundTime = now
    doPlayBeep()
  }
}

function doPlayBeep() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) return
    const ctx = new AudioCtx()

    // Two-tone beep (like WhatsApp)
    const playTone = (freq: number, start: number, duration: number) => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.frequency.value = freq
      osc.type = 'sine'
      gain.gain.setValueAtTime(0, ctx.currentTime + start)
      gain.gain.linearRampToValueAtTime(0.0975, ctx.currentTime + start + 0.01)
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration)
      osc.start(ctx.currentTime + start)
      osc.stop(ctx.currentTime + start + duration)
    }

    playTone(880, 0, 0.15)    // A5
    playTone(1320, 0.12, 0.2) // E6

    // Close context after sound finishes
    setTimeout(() => ctx.close(), 500)
  } catch {
    // ignore
  }
}

export function useNotifications() {
  const [unreadCount, setUnreadCount] = useState(0)
  const [lastNotification, setLastNotification] = useState<Record<string, unknown> | null>(null)
  const [connected, setConnected] = useState(false)
  const [mutedUsers, setMutedUsers] = useState(new Set<string>())
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pongPendingRef = useRef(false)
  const mutedUsersRef = useRef(new Set<string>())
  const unreadRequestRef = useRef(0)

  
  // Fetch initial unread count and muted users
  const fetchUnreadCount = useCallback(async () => {
    const requestId = ++unreadRequestRef.current
    try {
      const headers = getAuthHeaders()
      const [dmRes, channelsRes] = await Promise.all([
        fetch(`${API_URL}/api/dm`, { headers }),
        fetch(`${API_URL}/api/channels`, { headers }),
      ])
      let total = 0
      const dmData = await dmRes.json()
      total += (dmData.conversations || []).reduce((sum: number, c: { unread_count?: number }) => sum + (c.unread_count || 0), 0)
      if (dmData.muted) {
        const mutedSet = new Set<string>(dmData.muted)
        setMutedUsers(mutedSet)
        mutedUsersRef.current = mutedSet
      }
      // Count unread from channel contacts (WhatsApp, Telegram, etc.)
      if (channelsRes.ok) {
        const channelsData = await channelsRes.json()
        const channels = channelsData.channels || channelsData || []
        for (const ch of channels) {
          if (!ch.id) continue
          try {
            const contactsRes = await fetch(`${API_URL}/api/channel/contacts?channel_id=${ch.id}`, { headers })
            if (contactsRes.ok) {
              const contactsData = await contactsRes.json()
              const contacts = contactsData.contacts || contactsData || []
              // Only count unread from favorite contacts (matches sidebar badge behavior)
              total += contacts.reduce((sum: number, c: { is_favorite?: boolean; unread_count?: number }) => sum + (c.is_favorite ? (c.unread_count || 0) : 0), 0)
            }
          } catch { /* ignore */ }
        }
      }
      // Count unread from group channels (Atlas chat channels)
      const wsId = localStorage.getItem('atlas_ws_id')
      if (wsId) {
        try {
          const wsRes = await fetch(`${API_URL}/api/workspaces/${wsId}/channels`, { headers })
          if (wsRes.ok) {
            const wsData = await wsRes.json()
            const unreadMap = wsData.unread || {}
            for (const chId in unreadMap) {
              total += (unreadMap[chId] || 0)
            }
          }
        } catch { /* ignore */ }
      }
      if (requestId === unreadRequestRef.current) setUnreadCount(total)
    } catch {
      // ignore
    }
  }, [])

  const connect = useCallback(() => {
    const token = getToken()
    if (!token) return

    // Close existing connection
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }

    const ws = new WebSocket(`${WS_URL}/ws/notifications?token=${encodeURIComponent(token)}`)
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      // Start ping interval — with pong watchdog. If a ping goes
      // unanswered until the next cycle, the socket is silently dead
      // (half-open TCP after sleep/network change) — force reconnect.
      if (pingTimerRef.current) clearInterval(pingTimerRef.current)
      pongPendingRef.current = false
      pingTimerRef.current = setInterval(() => {
        if (ws.readyState !== WebSocket.OPEN) return
        if (pongPendingRef.current) {
          // Dead socket — detach onclose (it may take minutes to fire on
          // a blackholed TCP conn) and reconnect immediately.
          ws.onclose = null
          try { ws.close() } catch { /* ignore */ }
          if (wsRef.current === ws) wsRef.current = null
          setConnected(false)
          if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
          reconnectTimerRef.current = setTimeout(() => connect(), 1000)
          return
        }
        pongPendingRef.current = true
        ws.send(JSON.stringify({ type: 'ping' }))
      }, 25000)
      // Fetch initial unread count
      fetchUnreadCount()
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)
        if (msg.type === 'pong') {
          pongPendingRef.current = false
          return
        }

        if (msg.type === 'dm') {
          setLastNotification(msg)

          // Dispatch global event for components to listen
          window.dispatchEvent(new CustomEvent('dm-notification', { detail: msg }))

          // Check if sender is muted (from backend or local ref)
          const isMuted = msg.muted || mutedUsersRef.current.has(msg.sender_id)

          if (!isMuted) {
            // Play notification sound
            playNotificationSound()

            // Browser notification if permission granted
            if (Notification.permission === 'granted') {
              const senderName = msg.sender_name || `Usuário #${msg.sender_id}`
              const body = msg.content || (msg.attachment_name ? `Anexo: ${msg.attachment_name}` : 'Nova mensagem')
              new Notification(senderName, { body, icon: avatarUrl(msg.sender_avatar), tag: `dm-${msg.message_id}` })
            }
          }

          // Re-fetch unread count from backend for accuracy
          fetchUnreadCount()
        }

        if (msg.type === 'group_message') {
          setLastNotification(msg)

          // Dispatch global event for components to listen
          window.dispatchEvent(new CustomEvent('dm-notification', { detail: msg }))

          // Check if sender is muted
          const isMuted = mutedUsersRef.current.has(msg.sender_id)

          if (!isMuted) {
            playNotificationSound()

            if (Notification.permission === 'granted') {
              const senderName = msg.sender_name || `Usuário #${msg.sender_id}`
              const body = msg.content || 'Nova mensagem no canal'
              new Notification(`#${msg.channel_id} · ${senderName}`, { body, icon: avatarUrl(msg.sender_avatar), tag: `grp-${msg.message_id}` })
            }
          }

          // Re-fetch unread count from backend for accuracy
          fetchUnreadCount()
        }

        if (msg.type === 'channel_message') {
          // WhatsApp/Telegram message from agent
          setLastNotification(msg)
          window.dispatchEvent(new CustomEvent('dm-notification', { detail: msg }))

          // Only notify if the message is from someone else (not from_me)
          // and the contact is favorited (starred)
          if (!msg.from_me && msg.is_favorite) {
            playNotificationSound()

            if (Notification.permission === 'granted') {
              const senderName = msg.sender || msg.contact_name || 'WhatsApp'
              const body = msg.content || (msg.attachment_name ? `Anexo: ${msg.attachment_name}` : 'Nova mensagem')
              new Notification(senderName, { body, icon: avatarUrl(msg.avatar), tag: `ch-${msg.contact_id}` })
            }
          }

          // Re-fetch unread count so the title badge updates
          fetchUnreadCount()
        }

        if (msg.type === 'reaction') {
          // Dispatch global event for chat components to update reactions in real-time
          window.dispatchEvent(new CustomEvent('reaction-update', { detail: msg }))

          // Don't beep/badge the actor who triggered the reaction (multi-tab sync
          // still receives the event above so other tabs update the UI).
          let currentUserId: string | null = null
          try {
            currentUserId = JSON.parse(localStorage.getItem('atlas_user') || 'null')?.id
          } catch { /* ignore */ }
          if (msg.actor_id && msg.actor_id === currentUserId) return

          // Channel reactions: no sound, no browser notification, no unread bump.
          // Only DM reactions still beep (more personal, 1:1 context).
          if (msg.message_type === 'channel') {
            // Still dispatch the event for real-time UI updates, but skip
            // sound/notification/unread bump.
            return
          }

          // Check if the reactor is muted
          const isMuted = mutedUsers.has(msg.actor_id)
          if (!isMuted) {
            playNotificationSound()

            if (Notification.permission === 'granted') {
              const actorName = msg.actor_name || `Usuário #${msg.actor_id}`
              const emoji = msg.emoji || ''
              const body = `${emoji} na conversa`
              const tag = `reaction-${msg.message_type}-${msg.message_id}`
              new Notification(actorName, { body, icon: avatarUrl(msg.actor_avatar), tag })
            }
          }

          // Re-fetch unread count from backend so the badge reflects the bump.
          fetchUnreadCount()

          // For DMs, dispatch a dm-notification-like event so the DM sidebar
          // updates the per-conversation unread badge. The Layout handler keys
          // off `sender_id`, so we map other_user_id -> sender_id.
          if (msg.message_type === 'dm' && msg.other_user_id) {
            window.dispatchEvent(new CustomEvent('dm-notification', {
              detail: { ...msg, type: 'dm', sender_id: msg.other_user_id }
            }))
          }
        }

        if (msg.type === 'dm_edited' || msg.type === 'channel_edited') {
          window.dispatchEvent(new CustomEvent(msg.type, { detail: msg }))
        }

        // Call-incoming via notification WS (fallback for when Call WS is
        // disconnected or tab is in background on mobile)
        if (msg.type === 'call-incoming') {
          // Dispatch event for useCall hook to pick up
          window.dispatchEvent(new CustomEvent('call-incoming-notif', { detail: msg }))

          // Show browser notification so user sees the call even with tab in background
          if (Notification.permission === 'granted') {
            const callerName = msg.sender_name || 'Usuário'
            const callType = msg.is_video ? 'vídeo' : 'voz'
            const notif = new Notification(`Chamada de ${callType} — ${callerName}`, {
              body: 'Toque para atender',
              icon: avatarUrl(msg.sender_avatar),
              tag: `call-${msg.call_id}`,
              requireInteraction: true,
            })
            notif.onclick = () => {
              window.focus()
              notif.close()
            }
          }
        }

        if (msg.type === 'dm_deleted' || msg.type === 'channel_deleted' ||
            msg.type === 'dm_deleted_bulk' || msg.type === 'channel_deleted_bulk' ||
            msg.type === 'chat_deleted_bulk') {
          window.dispatchEvent(new CustomEvent(msg.type, { detail: msg }))
        }

        // Channel call started — dispatch event for useCall hook to pick up
        if (msg.type === 'channel-call-started') {
          window.dispatchEvent(new CustomEvent('channel-call-started', { detail: msg }))
        }

        // Call ended/rejected via notification WS — dispatch so useCall can
        // stop the ringtone even if the Call WS is disconnected
        if (msg.type === 'call-ended' || msg.type === 'call-rejected') {
          window.dispatchEvent(new CustomEvent(msg.type + '-notif', { detail: msg }))
        }

        // Channel call updated (someone joined or left) — dispatch for sidebar
        if (msg.type === 'channel-call-updated') {
          window.dispatchEvent(new CustomEvent('channel-call-updated', { detail: msg }))
        }

        // Channel call invite — someone invited us to join a channel call
        if (msg.type === 'channel-call-invite') {
          window.dispatchEvent(new CustomEvent('channel-call-invite', { detail: msg }))
          // Show browser notification
          if (Notification.permission === 'granted') {
            const callerName = msg.sender_name || 'Usuário'
            const callType = msg.is_video ? 'vídeo' : 'voz'
            const notif = new Notification(`Chamada de ${callType} — ${callerName}`, {
              body: 'Toque para atender',
              icon: avatarUrl(msg.sender_avatar),
              tag: `call-${msg.call_id}`,
              requireInteraction: true,
            })
            notif.onclick = () => {
              window.focus()
              notif.close()
            }
          }
        }

        // SFU forced join — someone force-joined us to a channel call.
        // This arrives via the notification WS when the call WS is not
        // connected (e.g. user just opened the page). Dispatch a custom
        // event so the CallContext can handle it.
        if (msg.type === 'sfu-forced-join') {
          window.dispatchEvent(new CustomEvent('sfu-forced-join', { detail: msg }))
        }

        // WhatsApp events from the desktop agent (relayed as type=event)
        const waEvents = ['whatsapp.qr', 'whatsapp.connected', 'whatsapp.disconnected', 'whatsapp.message', 'whatsapp.loggedout']
        if (msg.type === 'event' && waEvents.includes(msg.action)) {
          window.dispatchEvent(new CustomEvent(msg.action, { detail: { type: msg.action, ...msg, ...(typeof msg.payload === 'object' ? msg.payload : {}) } }))
        }

        // Agent status events
        if (msg.type === 'agent_status') {
          window.dispatchEvent(new CustomEvent('agent_status', { detail: msg }))
        }

        if (msg.type === 'task_notification') {
          // Dispatch global event for task notification bell
          window.dispatchEvent(new CustomEvent('task-notification', { detail: msg }))

          // Play notification sound
          playNotificationSound()

          // Browser notification
          if (Notification.permission === 'granted') {
            new Notification('Atlas — Task', {
              body: msg.message || 'Nova notificação de task',
              icon: avatarUrl(msg.actor_avatar),
              tag: `task-${msg.id}`,
            })
          }
        }

        if (msg.type === 'chat_notification') {
          window.dispatchEvent(new CustomEvent('chat-notification', { detail: msg }))
          playNotificationSound()
          if (Notification.permission === 'granted') {
            new Notification(`Atlas — ${msg.sender_name} em #${msg.channel_name}`, {
              body: msg.content || 'Você foi mencionado',
              tag: `chat-${msg.channel_id}`,
            })
          }
        }

        // Site uptime monitor — a monitored site went down or recovered.
        // Dispatch only: no sound/browser notification (sites monitoring
        // is disabled; kept for the sidebar badge listener).
        if (msg.type === 'site_status') {
          window.dispatchEvent(new CustomEvent('site-status-changed', { detail: msg }))
        }

        // Business Manager monitor — a tracked BM went down or recovered
        if (msg.type === 'account_status') {
          window.dispatchEvent(new CustomEvent('account-status-changed', { detail: msg }))
          playNotificationSound()
          if (Notification.permission === 'granted') {
            const down = msg.status === 'down'
            new Notification(down ? 'Atlas — Business Manager caiu' : 'Atlas — BM voltou', {
              body: down
                ? `${msg.account_name} (BM ${msg.bm_id})${msg.error ? ` — ${msg.error}` : ''}`
                : `${msg.account_name} (BM ${msg.bm_id}) voltou a responder`,
              tag: `account-${msg.account_id}`,
              requireInteraction: down,
            })
          }
        }

        if (msg.type === 'email') {
          window.dispatchEvent(new CustomEvent('email-notification', { detail: msg }))
          playNotificationSound()
          if (Notification.permission === 'granted') {
            new Notification('Atlas — Novo email', {
              body: msg.subject || 'Você recebeu um novo email',
              tag: 'email',
            })
          }
        }
      } catch {
        // ignore
      }
    }

    ws.onclose = () => {
      setConnected(false)
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current)
        pingTimerRef.current = null
      }
      // Reconnect after 3 seconds
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      reconnectTimerRef.current = setTimeout(() => connect(), 3000)
    }

    ws.onerror = () => {
      setConnected(false)
    }
  }, [fetchUnreadCount])

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    connect()
    return () => {
      if (wsRef.current) {
        wsRef.current.close()
        wsRef.current = null
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current)
      if (pingTimerRef.current) clearInterval(pingTimerRef.current)
    }
  }, [connect])

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  // Periodically sync unread count from backend (every 30s)
  useEffect(() => {
    const interval = setInterval(fetchUnreadCount, 30000)
    return () => clearInterval(interval)
  }, [fetchUnreadCount])

  // Mark messages as read (decrement unread count)
  const markAsRead = useCallback((count = 1) => {
    setUnreadCount(prev => Math.max(0, prev - count))
  }, [])

  // Reset unread count (e.g. when opening messages view)
  const resetUnread = useCallback(() => {
    fetchUnreadCount()
  }, [fetchUnreadCount])

  // Mute a user
  const muteUser = useCallback(async (userId: string) => {
    setMutedUsers(prev => new Set([...prev, userId]))
    mutedUsersRef.current = new Set([...mutedUsersRef.current, userId])
    try {
      await fetch(`${API_URL}/api/dm/mute/${userId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })
    } catch {
      // ignore
    }
  }, [])

  // Unmute a user
  const unmuteUser = useCallback(async (userId: string) => {
    setMutedUsers(prev => {
      const next = new Set(prev)
      next.delete(userId)
      return next
    })
    const nextRef = new Set(mutedUsersRef.current)
    nextRef.delete(userId)
    mutedUsersRef.current = nextRef
    try {
      await fetch(`${API_URL}/api/dm/mute/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      })
    } catch {
      // ignore
    }
  }, [])

  return { unreadCount, lastNotification, connected, markAsRead, resetUnread, fetchUnreadCount, mutedUsers, muteUser, unmuteUser }
}
